import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import AirportDropdownOption from './AirportDropdownOption'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import Input from 'components/Common/Form/Input/Input'
import NoAirportsOption from './NoAirportsOption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const AirportOptions = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  min-height: ${rem(80)};
`

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.primary.normal};
  padding: ${rem(20)} ${rem(24)};
`

const SearchWrapper = styled(VerticalSpacer)`
  padding: ${rem(8)} ${rem(20)} ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.five};
`

const StyledInput = styled(Input)`
  height: ${rem(20)};
  padding: 0;
  font-size: ${rem(14)};
  margin-left: ${rem(24)};
  width: 100%;
  border: none;

  &::placeholder {
    color: ${({ theme }) => theme.palette.neutral.two};
  }
`

interface Props {
  open?: boolean;
  loading: boolean;
  inputValue: string;
  airports: Array<App.AirportLocation>;
  heading?: string;
  airportIcon?: React.ReactNode;
  className?: string;
  onSelectAirport: (airport: App.AirportLocation) => void;
  onChangeText: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function AirportSelectOptions(props: Props) {
  const {
    open,
    loading,
    inputValue,
    airports,
    heading,
    airportIcon,
    className,
    onSelectAirport,
    onChangeText,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)

  const onChangeAirport = useCallback((airport: App.AirportLocation) => {
    onSelectAirport(airport)
  }, [onSelectAirport])

  const noAirportOptions = !loading && airports.length === 0 && inputValue.length > 2

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    }
  }, [open])

  return (
    <div className={className}>
      <SearchWrapper gap={12}>
        <SearchBox>
          <LineSearchIcon colour="primary" />
          <StyledInput
            placeholder="Search by city or airport"
            value={inputValue}
            onChange={onChangeText}
            ref={inputRef}
          />
        </SearchBox>
        <BodyText variant="medium" colour="neutral-two">{heading}</BodyText>
      </SearchWrapper>
      <AirportOptions>
        <LoadingIndicator visible={loading} floating>
          Loading
        </LoadingIndicator>
        {!loading && airports?.map(airport =>
          <AirportDropdownOption
            key={airport.id}
            subRecord={airport.isCityAirport}
            airport={airport}
            onSelectAirport={onChangeAirport}
            icon={airportIcon}
          />,
        )}
        {noAirportOptions && <NoAirportsOption inputValue={inputValue} />}
      </AirportOptions>
    </div>
  )
}

export default AirportSelectOptions
